/**
 * The confirmation round's state (3.3) as one icon on the visit card — the same
 * set on My week and on My day, so a card reads the same wherever it is drawn
 * (Pete, 2026-09-19: *"icons on the visits themselves. Same set on the week view
 * and the day view"*).
 *
 * It replaces both the Tomorrow's times panel's chip and the bracket My day used
 * to draw down a card's left edge. The bracket was a 5px-wide box with a 3px
 * dashed border, which renders as a column of dashes whatever colour it is
 * given; there is nothing to fix about a shape that small, so it is gone.
 *
 * The words live in the tooltip, not on the card: the whole row label — state
 * and every reason it carries — plus the window the text actually named, so the
 * one thing the panel said that the card cannot is still one hover away.
 */
import { Box, Tooltip } from "@mui/material";
import CheckCircle from "@mui/icons-material/CheckCircle";
import PhoneInTalk from "@mui/icons-material/PhoneInTalk";
import ScheduleSend from "@mui/icons-material/ScheduleSend";
import SmsOutlined from "@mui/icons-material/SmsOutlined";
import type { RoundStatus } from "@caredna-marlin/utils/homeHealthDemo/state";

/**
 * One icon per state, and each says what it means rather than colouring a dot:
 * a text that has not gone, a text waiting on an answer, an answer, and a call
 * somebody has to make. Whatever the patient actually wrote is in the tooltip
 * with the rest of the row label, which is the only place it can be read — the
 * round knows a yes and nothing else about a message.
 */
const ICON = {
  notSent: ScheduleSend,
  pending: SmsOutlined,
  confirmed: CheckCircle,
  needsCall: PhoneInTalk,
} as const satisfies Record<RoundStatus, unknown>;

/** The same families the chip used, as palette paths rather than Chip colours. */
const COLOR: Record<RoundStatus, string> = {
  notSent: "grey.600",
  pending: "warning.main",
  confirmed: "success.main",
  needsCall: "error.main",
};

export function ConfirmationIcon({ status, label, fontSize = 15 }: { status: RoundStatus; label?: string; fontSize?: number }) {
  const Icon = ICON[status];
  return (
    <Tooltip title={label ?? ""}>
      <Box component="span" data-round-status={status} aria-label={label} sx={{ display: "inline-flex", alignItems: "center", lineHeight: 0 }}>
        <Icon sx={{ fontSize, color: COLOR[status] }} />
      </Box>
    </Tooltip>
  );
}
